/**
 * Document Processing Hook
 *
 * Headless hook for document upload, entity extraction, and graph creation.
 * Can be used by Documents2Page, Discovery Assistant, Canvas, or any other UI.
 *
 * Features:
 * - Document upload (file + paste)
 * - Entity extraction with caching
 * - Graph entity creation
 * - Progress tracking
 * - Error handling
 */

import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  documents2Api,
  type Document2ListItem,
  type Document2WithEntities,
  type Document2Entity,
  type ContentType,
} from '@/lib/api/client'
import { getCurrentAccountId, getCurrentProjectId } from '@/lib/api/sparql-adapter'
import { useAIStore } from '@/stores/aiStore'
import { extractTextFromPdf } from '@/lib/pdf/extractor'

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Extract JSON from Claude's markdown code block response
 * Claude returns: ```json\n{...}\n```
 */
function extractJsonFromMarkdown(text: string): string {
  // Remove markdown code block markers
  const match = text.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/)
  if (match) {
    return match[1].trim()
  }
  // If no code block, return as-is (might already be plain JSON)
  return text.trim()
}

// ============================================================================
// TYPES
// ============================================================================

export type ExtractionStage = 'idle' | 'analyzing' | 'saving' | 'complete' | 'error'

export interface ExtractionProgress {
  stage: ExtractionStage
  entitiesFound: number
  relationshipsFound: number
  fromCache: boolean
  error?: string
}

export interface UseDocumentProcessingOptions {
  /** Auto-load documents list on mount */
  autoLoadDocuments?: boolean

  /** Callback when document is created */
  onDocumentCreated?: (doc: Document2WithEntities) => void

  /** Callback when entities are extracted */
  onEntitiesExtracted?: (entities: Document2Entity[], fromCache: boolean) => void

  /** Callback when graph entities are created */
  onGraphEntitiesCreated?: (count: number) => void

  /** Error handler */
  onError?: (error: Error) => void
}

export interface UseDocumentProcessingReturn {
  // ========== STATE ==========
  /** List of all documents */
  documents: Document2ListItem[]

  /** Currently selected document with entities */
  selectedDoc: Document2WithEntities | null

  /** Loading states */
  isLoadingDocuments: boolean
  isLoadingDocument: boolean

  /** Extraction progress */
  extractionProgress: ExtractionProgress

  /** Cache statistics */
  cacheStats: {
    total: number
    cached: number
    cacheHitRate: number
  } | null

  /** AI configuration status */
  isConfigured: boolean

  /** General error */
  error: Error | null

  // ========== ACTIONS ==========
  /** Upload a file and create document */
  uploadDocument: (
    file: File,
    contentType: ContentType,
    title?: string
  ) => Promise<Document2WithEntities>

  /** Paste content and create document */
  pasteContent: (
    title: string,
    content: string,
    contentType: ContentType
  ) => Promise<Document2WithEntities>

  /** Extract entities from document content */
  extractEntities: (
    docId: string,
    useCache?: boolean
  ) => Promise<{ entities: Document2Entity[]; fromCache: boolean }>

  /** Create graph entities from selected entities */
  createGraphEntities: (
    docId: string,
    entityIds?: string[]
  ) => Promise<number>

  /** Select a document (loads full details) */
  selectDocument: (docId: string) => Promise<void>

  /** Invalidate cache for a document */
  invalidateCache: (docId: string) => Promise<void>

  /** Refresh documents list */
  refreshDocuments: () => Promise<void>

  /** Refresh cache stats */
  refreshCacheStats: () => Promise<void>

  /** Clear error */
  clearError: () => void

  /** Reset extraction progress */
  resetProgress: () => void
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useDocumentProcessing(
  options: UseDocumentProcessingOptions = {}
): UseDocumentProcessingReturn {
  const {
    autoLoadDocuments = true,
    onDocumentCreated,
    onEntitiesExtracted,
    onGraphEntitiesCreated,
    onError,
  } = options

  const { apiKey, isConfigured } = useAIStore()
  const queryClient = useQueryClient()

  // Local state
  const [extractionProgress, setExtractionProgress] = useState<ExtractionProgress>({
    stage: 'idle',
    entitiesFound: 0,
    relationshipsFound: 0,
    fromCache: false,
  })
  const [error, setError] = useState<Error | null>(null)

  // Get account/project context
  const accountId = getCurrentAccountId()
  const projectId = getCurrentProjectId()
  const hasContext = !!accountId && !!projectId

  // ========== QUERIES ==========

  // Fetch documents list
  const {
    data: documents = [],
    isLoading: isLoadingDocuments,
    error: documentsError,
  } = useQuery({
    queryKey: ['documents2', 'list', accountId, projectId],
    queryFn: async () => {
      if (!accountId || !projectId) {
        throw new Error('Missing account or project context')
      }
      return documents2Api.list(accountId, projectId)
    },
    enabled: hasContext && autoLoadDocuments,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })

  // Fetch cache statistics
  // TODO: Re-enable when backend endpoint is implemented
  const { data: cacheStats } = useQuery({
    queryKey: ['documents2', 'cacheStats', accountId, projectId],
    queryFn: async () => {
      if (!accountId || !projectId) {
        throw new Error('Missing account or project context')
      }
      return documents2Api.getCacheStats(accountId, projectId)
    },
    enabled: false, // Disabled until backend endpoint exists
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // ========== MUTATIONS ==========

  // Create document mutation
  const createDocumentMutation = useMutation({
    mutationFn: async (data: {
      title: string
      contentType: ContentType
      rawContent: string
      markdownContent?: string
      fileName?: string
      fileType?: 'pdf' | 'md' | 'txt'
    }) => {
      if (!accountId || !projectId) {
        throw new Error('Missing account or project context')
      }
      return documents2Api.create(accountId, projectId, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents2', 'list'] })
    },
    onError: (err) => {
      setError(err as Error)
      onError?.(err as Error)
    },
  })

  // Extract entities mutation
  const extractEntitiesMutation = useMutation({
    mutationFn: async (data: {
      docId: string
      content: string
      contentType: ContentType
      useCache: boolean
    }) => {
      if (!accountId || !projectId || !apiKey) {
        throw new Error('Missing context or API key')
      }

      setExtractionProgress({
        stage: 'analyzing',
        entitiesFound: 0,
        relationshipsFound: 0,
        fromCache: false,
      })

      // Call analyze-cached endpoint
      const result = await documents2Api.analyzeWithCache(accountId, projectId, {
        sourceId: data.docId,
        apiKey,
        content: data.content,
        contentType: data.contentType,
        maxTokens: 16384,
        useCache: data.useCache,
      })

      return result
    },
    onSuccess: (result) => {
      // Parse JSON response (extract from markdown code blocks if needed)
      const textContent = result.content[0]?.text || '{}'
      const jsonString = extractJsonFromMarkdown(textContent)
      const parsed = JSON.parse(jsonString)

      setExtractionProgress({
        stage: 'saving',
        entitiesFound: parsed.entities?.length || 0,
        relationshipsFound: parsed.relationships?.length || 0,
        fromCache: result.cached,
      })

      return parsed
    },
    onError: (err) => {
      setExtractionProgress((prev) => ({
        ...prev,
        stage: 'error',
        error: (err as Error).message,
      }))
      setError(err as Error)
      onError?.(err as Error)
    },
  })

  // Save entities mutation
  const saveEntitiesMutation = useMutation({
    mutationFn: async (data: {
      docId: string
      entities: Array<{
        id: string
        type: string
        name: string
        description?: string
        confidence?: number
        evidence: Array<{ quote: string; position?: number }>
      }>
      relationships?: Array<{
        from: string
        to: string
        type: string
        evidence?: string
        confidence?: number
      }>
    }) => {
      if (!accountId || !projectId) {
        throw new Error('Missing account or project context')
      }
      return documents2Api.saveEntities(accountId, projectId, data.docId, data)
    },
    onSuccess: (_result, variables) => {
      setExtractionProgress((prev) => ({
        ...prev,
        stage: 'complete',
      }))

      queryClient.invalidateQueries({ queryKey: ['documents2', 'document', variables.docId] })
      queryClient.invalidateQueries({ queryKey: ['documents2', 'list'] })
    },
    onError: (err) => {
      setExtractionProgress((prev) => ({
        ...prev,
        stage: 'error',
        error: (err as Error).message,
      }))
      setError(err as Error)
      onError?.(err as Error)
    },
  })

  // Create graph entities mutation
  const createGraphEntitiesMutation = useMutation({
    mutationFn: async (data: { docId: string; entityIds?: string[] }) => {
      if (!accountId || !projectId) {
        throw new Error('Missing account or project context')
      }

      if (data.entityIds) {
        return documents2Api.createGraphEntities(accountId, projectId, data.docId, data.entityIds)
      } else {
        return documents2Api.createAllGraphEntities(accountId, projectId, data.docId)
      }
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documents2', 'document', variables.docId] })
      onGraphEntitiesCreated?.(result.entitiesCreated)
    },
    onError: (err) => {
      setError(err as Error)
      onError?.(err as Error)
    },
  })

  // Invalidate cache mutation
  const invalidateCacheMutation = useMutation({
    mutationFn: async (docId: string) => {
      if (!accountId || !projectId) {
        throw new Error('Missing account or project context')
      }
      return documents2Api.invalidateCache(accountId, projectId, docId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents2', 'cacheStats'] })
    },
    onError: (err) => {
      setError(err as Error)
      onError?.(err as Error)
    },
  })

  // ========== ACTIONS ==========

  const uploadDocument = useCallback(async (
    file: File,
    contentType: ContentType,
    title?: string
  ): Promise<Document2WithEntities> => {
    if (!isConfigured) {
      throw new Error('Claude API key not configured')
    }

    // Determine file type
    const isPdf = file.name.toLowerCase().endsWith('.pdf')
    const fileType = isPdf
      ? 'pdf'
      : file.name.endsWith('.md')
      ? 'md'
      : 'txt'

    // Read file content with proper handling for PDFs
    let content: string
    if (isPdf) {
      const extracted = await extractTextFromPdf(file)
      content = extracted.fullText // Uses sanitized text
    } else {
      content = await file.text()
    }

    // Create document
    const doc = await createDocumentMutation.mutateAsync({
      title: title || file.name,
      contentType,
      rawContent: content,
      markdownContent: content,
      fileName: file.name,
      fileType,
    })

    // Load full document with entities
    const fullDoc = await documents2Api.get(accountId!, projectId!, doc.id)

    onDocumentCreated?.(fullDoc)
    return fullDoc
  }, [isConfigured, accountId, projectId, createDocumentMutation, onDocumentCreated])

  const pasteContent = useCallback(async (
    title: string,
    content: string,
    contentType: ContentType
  ): Promise<Document2WithEntities> => {
    if (!isConfigured) {
      throw new Error('Claude API key not configured')
    }

    // Create document
    const doc = await createDocumentMutation.mutateAsync({
      title,
      contentType,
      rawContent: content,
      markdownContent: content,
    })

    // Load full document with entities
    const fullDoc = await documents2Api.get(accountId!, projectId!, doc.id)

    onDocumentCreated?.(fullDoc)
    return fullDoc
  }, [isConfigured, accountId, projectId, createDocumentMutation, onDocumentCreated])

  const extractEntities = useCallback(async (
    docId: string,
    useCache: boolean = true
  ): Promise<{ entities: Document2Entity[]; fromCache: boolean }> => {
    if (!isConfigured) {
      throw new Error('Claude API key not configured')
    }

    // Get document
    const doc = await documents2Api.get(accountId!, projectId!, docId)

    // Extract entities
    const result = await extractEntitiesMutation.mutateAsync({
      docId,
      content: doc.document.rawContent,
      contentType: doc.document.contentType,
      useCache,
    })

    // Parse response (extract from markdown code blocks if needed)
    const textContent = result.content[0]?.text || '{}'
    const jsonString = extractJsonFromMarkdown(textContent)
    const parsed = JSON.parse(jsonString)

    // Save entities
    await saveEntitiesMutation.mutateAsync({
      docId,
      entities: parsed.entities || [],
      relationships: parsed.relationships || [],
    })

    // Get updated document with saved entities
    const updatedDoc = await documents2Api.get(accountId!, projectId!, docId)

    onEntitiesExtracted?.(updatedDoc.entities, result.cached)

    return {
      entities: updatedDoc.entities,
      fromCache: result.cached,
    }
  }, [
    isConfigured,
    accountId,
    projectId,
    extractEntitiesMutation,
    saveEntitiesMutation,
    onEntitiesExtracted,
  ])

  const createGraphEntities = useCallback(async (
    docId: string,
    entityIds?: string[]
  ): Promise<number> => {
    const result = await createGraphEntitiesMutation.mutateAsync({ docId, entityIds })
    return result.entitiesCreated
  }, [createGraphEntitiesMutation])

  // Local state for selected document ID
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)

  // Fetch selected document
  const { data: selectedDoc } = useQuery({
    queryKey: ['documents2', 'document', selectedDocId],
    queryFn: async () => {
      if (!accountId || !projectId || !selectedDocId) {
        return null
      }
      return documents2Api.get(accountId, projectId, selectedDocId)
    },
    enabled: !!accountId && !!projectId && !!selectedDocId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })

  const selectDocument = useCallback(async (docId: string): Promise<void> => {
    setSelectedDocId(docId)
  }, [])

  const invalidateCache = useCallback(async (docId: string): Promise<void> => {
    await invalidateCacheMutation.mutateAsync(docId)
  }, [invalidateCacheMutation])

  const refreshDocuments = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['documents2', 'list'] })
  }, [queryClient])

  const refreshCacheStats = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['documents2', 'cacheStats'] })
  }, [queryClient])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const resetProgress = useCallback(() => {
    setExtractionProgress({
      stage: 'idle',
      entitiesFound: 0,
      relationshipsFound: 0,
      fromCache: false,
    })
  }, [])

  return {
    // State
    documents,
    selectedDoc: selectedDoc || null,
    isLoadingDocuments,
    isLoadingDocument: extractEntitiesMutation.isPending || saveEntitiesMutation.isPending,
    extractionProgress,
    cacheStats: cacheStats || null,
    isConfigured,
    error: error || (documentsError as Error | null),

    // Actions
    uploadDocument,
    pasteContent,
    extractEntities,
    createGraphEntities,
    selectDocument,
    invalidateCache,
    refreshDocuments,
    refreshCacheStats,
    clearError,
    resetProgress,
  }
}
